// hooks/useEventSlotEdit.js
import { useState, useCallback } from "react";
import { managementService } from "../services/managementService.js";
import { SLOT_STATUSES } from "../utils";

export function useEventSlotEdit(event, slot, onSuccess) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Synchroniser les instructeurs
  const syncInstructors = useCallback(
    async (slotId, newInstructorIds, oldInstructorIds) => {
      const toAdd = newInstructorIds.filter(
        (id) => !oldInstructorIds.includes(id),
      );
      const toRemove = oldInstructorIds.filter(
        (id) => !newInstructorIds.includes(id),
      );

      const promises = [];

      if (toAdd.length > 0) {
        promises.push(managementService.assignInstructors(slotId, toAdd));
      }

      if (toRemove.length > 0) {
        promises.push(managementService.removeInstructors(slotId, toRemove));
      }

      await Promise.all(promises);
    },
    [],
  );

  // Synchroniser les participants
  const syncParticipants = useCallback(
    async (slotId, newParticipants, oldParticipants) => {
      // Normaliser oldParticipants pour avoir user_id cohérent
      const normalizedOldParticipants = oldParticipants.map((p) => ({
        user_id: p.user_id || p.users?.id,
        status: p.status,
      }));

      const oldUserIds = normalizedOldParticipants.map((p) => p.user_id);
      const newUserIds = newParticipants.map((p) => p.user_id);

      // Participants à ajouter
      const toAdd = newParticipants.filter(
        (p) => !oldUserIds.includes(p.user_id),
      );

      // Participants à supprimer
      const toRemove = normalizedOldParticipants.filter(
        (p) => !newUserIds.includes(p.user_id),
      );

      // Participants dont le statut a changé
      const toUpdate = newParticipants.filter((newP) => {
        const oldP = normalizedOldParticipants.find(
          (old) => old.user_id === newP.user_id,
        );
        return oldP && oldP.status !== newP.status;
      });

      const promises = [];

      // Ajouter nouveaux participants en batch
      if (toAdd.length > 0) {
        promises.push(
          managementService.addParticipants(
            slotId,
            toAdd.map((p) => ({
              user_id: p.user_id,
              status: p.status,
            })),
          ),
        );
      }

      // Supprimer participants retirés en batch
      if (toRemove.length > 0) {
        const participantIdsToRemove = toRemove
          .map((oldP) => {
            // Chercher dans slot.participants avec user_id OU users.id
            const participantRecord = slot.participants.find(
              (sp) =>
                sp.user_id === oldP.user_id || sp.users?.id === oldP.user_id,
            );
            return participantRecord?.id;
          })
          .filter(Boolean);

        if (participantIdsToRemove.length > 0) {
          promises.push(
            managementService.removeParticipants(participantIdsToRemove),
          );
        }
      }

      // Mettre à jour les statuts
      if (toUpdate.length > 0) {
        for (const newP of toUpdate) {
          // Chercher dans slot.participants avec user_id OU users.id
          const participantRecord = slot.participants.find(
            (sp) =>
              sp.user_id === newP.user_id || sp.users?.id === newP.user_id,
          );

          if (participantRecord?.id) {
            promises.push(
              managementService.updateParticipantStatus(
                participantRecord.id,
                newP.status,
              ),
            );
          }
        }
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }
    },
    [slot],
  );

  // Mise à jour de l'event/slot/recurrence
  const updateEventSlot = useCallback(
    async (
      formData,
      initialInstructorIds,
      initialParticipants,
      saveRecurrenceFn = null,
    ) => {
      if (!event || !slot) {
        throw new Error("Event et Slot requis pour l'édition");
      }

      setSubmitting(true);
      setError(null);

      const eventPayload = {
        name: formData.name,
        event_type: formData.event_type,
      };

      const slotPayload = {
        slot_date: formData.slot_date,
        start_time:
          formData.is_all_day || !formData.start_time
            ? null
            : formData.start_time,
        end_time:
          formData.is_all_day || !formData.end_time ? null : formData.end_time,
        is_all_day: formData.is_all_day,
        slot_status: formData.slot_status,
      };

      try {
        // 1. Mettre à jour event et slot en parallèle
        await Promise.all([
          managementService.updateEvent(event.id, eventPayload),
          managementService.updateSlot(slot.id, slotPayload),
        ]);

        // 2. Sauvegarder la récurrence si la fonction est fournie
        let savedRecurrence = null;
        if (saveRecurrenceFn && typeof saveRecurrenceFn === "function") {
          try {
            const result = await saveRecurrenceFn(formData); // Passer formData
            savedRecurrence = result?.data || null;
          } catch (recurrenceError) {
            console.error(
              "Erreur sauvegarde récurrence (non bloquant):",
              recurrenceError,
            );
          }
        }

        // 3. Synchroniser instructeurs et participants en parallèle
        await Promise.all([
          syncInstructors(
            slot.id,
            formData.instructor_ids,
            initialInstructorIds,
          ),
          syncParticipants(slot.id, formData.participants, initialParticipants),
        ]);

        if (onSuccess) {
          onSuccess({ recurrence: savedRecurrence });
        }
      } catch (err) {
        const errorMessage = err.message || "Erreur lors de la modification";
        setError(errorMessage);
        console.error("Erreur édition event/slot:", err);
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [event, slot, onSuccess, syncInstructors, syncParticipants],
  );

  return {
    updateEventSlot,
    submitting,
    error,
  };
}
